<template>
	<div class="document-viewer">
		<header class="document-viewer__header">
			<button class="document-viewer__back" type="button" @click="goBack">
				Назад
			</button>
			<div class="document-viewer__title" :title="title">{{ title }}</div>
			<span
				v-if="downloadDisabled"
				class="document-viewer__restriction"
			>
				Скачивание отключено преподавателем
			</span>
			<div class="document-viewer__actions">
				<a
					v-if="fileUrl && !downloadDisabled"
					:href="downloadUrl"
					class="document-viewer__action"
					target="_blank"
					rel="noopener noreferrer"
				>
					Открыть в новой вкладке
				</a>
				<a
					v-if="fileUrl && !downloadDisabled"
					:href="downloadUrl"
					:download="downloadName"
					class="document-viewer__action document-viewer__action--primary"
				>
					Скачать
				</a>
			</div>
		</header>

		<section class="document-viewer__body">
			<div v-if="!fileUrl" class="document-viewer__empty">
				Файл не найден или ссылка недоступна.
			</div>
			<template v-else>
				<div
					v-if="isImage"
					class="document-viewer__image-viewer"
					@contextmenu="preventProtectedAction"
					@copy="preventProtectedAction"
					@cut="preventProtectedAction"
					@dragstart="preventProtectedAction"
					@selectstart="preventProtectedAction"
				>
					<div class="document-viewer__zoom-controls">
						<button
							type="button"
							class="document-viewer__zoom-btn"
							:disabled="imageZoom <= IMAGE_ZOOM_MIN"
							@click="changeImageZoom(-10)"
						>
							-
						</button>
						<span class="document-viewer__zoom-label">{{ imageZoom }}%</span>
						<button
							type="button"
							class="document-viewer__zoom-btn"
							:disabled="imageZoom >= IMAGE_ZOOM_MAX"
							@click="changeImageZoom(10)"
						>
							+
						</button>
						<button
							type="button"
							class="document-viewer__zoom-reset"
							:disabled="imageZoom === 100"
							@click="resetImageZoom"
						>
							100%
						</button>
					</div>
					<div
						class="document-viewer__image-stage"
						:class="{ 'document-viewer__image-stage--protected': downloadDisabled }"
					>
						<img
							:src="fileUrl"
							:alt="title"
							class="document-viewer__image"
							:style="imageZoomStyle"
						/>
						<div
							v-if="downloadDisabled"
							class="document-viewer__protected-watermark"
							aria-hidden="true"
						>
							Protected View
						</div>
					</div>
				</div>
				<DocxViewer
					v-else-if="isDocx"
					:file-url="fileUrl"
					:download-name="downloadName"
					:download-disabled="downloadDisabled"
				/>
				<PdfViewer
					v-else-if="isPdf"
					:file-url="fileUrl"
					:download-disabled="downloadDisabled"
				/>
				<PptxViewer 
					v-else-if="isPowerPoint && !isLikelyProtectedUrl && !downloadDisabled" 
					:file-url="fileUrl"
				/>
				<div v-else-if="isPowerPoint && (isLikelyProtectedUrl || downloadDisabled)" class="document-viewer__office-wrapper">
					<div class="document-viewer__local-notice">
						<p class="document-viewer__notice-title">Предпросмотр презентации недоступен</p>
						<p class="document-viewer__notice-text">
							Этот файл защищён (требует авторизацию). Внешний онлайн-просмотрщик не может получить доступ к приватному файлу.
							<span v-if="!downloadDisabled">Используйте кнопку "Скачать" для просмотра файла.</span>
							<span v-else>Скачивание отключено преподавателем.</span>
						</p>
						<a
							v-if="!downloadDisabled"
							:href="downloadUrl"
							:download="downloadName"
							class="document-viewer__download-btn"
						>
							Скачать файл
						</a>
					</div>
				</div>
				<div v-else-if="isWord || isExcel" class="document-viewer__office-wrapper">
					<div v-if="isLocalFile" class="document-viewer__local-notice">
						<p class="document-viewer__notice-title">Предварительный просмотр недоступен для локальных файлов</p>
						<p class="document-viewer__notice-text">
							Файлы на localhost не могут быть открыты в онлайн-просмотрщике.
							Используйте кнопку "Скачать" для просмотра файла.
						</p>
						<a
							v-if="!downloadDisabled"
							:href="downloadUrl"
							:download="downloadName"
							class="document-viewer__download-btn"
						>
							Скачать файл
						</a>
					</div>
					<div
						v-else-if="isWord"
						class="document-viewer__local-notice"
					>
						<p class="document-viewer__notice-title">Предпросмотр .doc недоступен</p>
						<p class="document-viewer__notice-text">
							Формат .doc (старый Word) не поддерживается для встроенного предпросмотра. Скачайте файл для просмотра.
						</p>
						<a
							v-if="!downloadDisabled"
							:href="downloadUrl"
							:download="downloadName"
							class="document-viewer__download-btn"
						>
							Скачать файл
						</a>
					</div>
					<div
						v-else-if="isExcel && isLikelyProtectedUrl"
						class="document-viewer__local-notice"
					>
						<p class="document-viewer__notice-title">Предпросмотр таблицы недоступен</p>
						<p class="document-viewer__notice-text">
							Этот файл защищён (требует авторизацию). Office Online Viewer не может получить доступ к приватному файлу.
							<span v-if="!downloadDisabled">Используйте кнопку "Скачать" для просмотра файла.</span>
							<span v-else>Скачивание отключено преподавателем.</span>
						</p>
						<a
							v-if="!downloadDisabled"
							:href="downloadUrl"
							:download="downloadName"
							class="document-viewer__download-btn"
						>
							Скачать файл
						</a>
					</div>
					<iframe
						v-else-if="!downloadDisabled"
						class="document-viewer__frame"
						:src="viewerUrl"
						title="Просмотр документа"
						referrerpolicy="no-referrer"
					></iframe>
					<div v-else class="document-viewer__local-notice">
						<p class="document-viewer__notice-title">Предпросмотр недоступен</p>
						<p class="document-viewer__notice-text">Скачивание отключено преподавателем.</p>
					</div>
				</div>
				<div
					v-else
					class="document-viewer__frame-wrapper"
					@contextmenu="preventProtectedAction"
					@copy="preventProtectedAction"
					@cut="preventProtectedAction"
					@dragstart="preventProtectedAction"
					@selectstart="preventProtectedAction"
				>
					<iframe
						class="document-viewer__frame"
						:src="viewerUrl"
						title="Просмотр документа"
						referrerpolicy="no-referrer"
					></iframe>
					<div
						v-if="downloadDisabled && isPdf"
						class="document-viewer__frame-shield"
						aria-hidden="true"
					></div>
				</div>
				<div v-if="!isPreviewSupported" class="document-viewer__hint">
					Этот формат может не поддерживаться браузером. Используйте кнопку
					"Открыть в новой вкладке".
				</div>
			</template>
		</section>
	</div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PptxViewer from '@/components/viewers/PptxViewer.vue'
import DocxViewer from '@/components/viewers/DocxViewer.vue'
import PdfViewer from '@/components/viewers/PdfViewer.vue'

const route = useRoute()
const router = useRouter()

const rawUrl = computed(() => {
	const value = route.query.url
	return Array.isArray(value) ? value[0] : value
})

const rawDownloadUrl = computed(() => {
	const value = route.query.downloadUrl
	return Array.isArray(value) ? value[0] : value
})

const title = computed(() => {
	const value = route.query.title
	const text = Array.isArray(value) ? value[0] : value
	return text ? String(text) : 'Документ'
})

const fileUrl = computed(() => {
	const value = rawUrl.value
	if (!value) return ''
	const asString = String(value)
	try {
		return decodeURIComponent(asString)
	} catch (error) {
		return asString
	}
})

const downloadUrl = computed(() => {
	const value = rawDownloadUrl.value || rawUrl.value
	if (!value) return ''
	const asString = String(value)
	try {
		return decodeURIComponent(asString)
	} catch (error) {
		return asString
	}
})

const fileExt = computed(() => {
	const url = fileUrl.value
	if (!url) return ''
	const cleanUrl = url.split('#')[0].split('?')[0]
	const parts = cleanUrl.split('.')
	if (parts.length < 2) return ''
	return parts.pop().toLowerCase()
})

const isImage = computed(() =>
	['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExt.value)
)
const isPdf = computed(() => fileExt.value === 'pdf')
const isPowerPoint = computed(() => ['ppt', 'pptx'].includes(fileExt.value))
const isWord = computed(() => ['doc', 'docx'].includes(fileExt.value))
const isExcel = computed(() => ['xls', 'xlsx'].includes(fileExt.value))
const isDocx = computed(() => fileExt.value === 'docx')
const isPreviewSupported = computed(() => 
	isImage.value || isPdf.value || isPowerPoint.value || isWord.value || isExcel.value
)
const IMAGE_ZOOM_MIN = 25
const IMAGE_ZOOM_MAX = 300
const imageZoom = ref(100)

const imageZoomStyle = computed(() => ({
	'--image-zoom': String(imageZoom.value / 100),
	zoom: String(imageZoom.value / 100),
}))

const isLikelyProtectedUrl = computed(() => {
	const url = fileUrl.value
	if (!url) return false
	return url.includes('/api/') || url.startsWith('/api/') || url.includes('/download')
})

const downloadDisabled = computed(() => {
	const value = route.query.downloadDisabled
	const text = Array.isArray(value) ? value[0] : value
	if (!text) return false
	return ['1', 'true', 'yes'].includes(String(text).toLowerCase())
})

const viewerUrl = computed(() => {
	const url = fileUrl.value
	if (!url) return ''
	
	// Для Word и Excel используем Office Online Viewer
	if (isWord.value || isExcel.value) {
		const isLocalhost = url.includes('localhost') || url.includes('127.0.0.1')
		
		if (isLocalhost) {
			return url
		} else {
			const encodedUrl = encodeURIComponent(url)
			return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`
		}
	}
	
	// Для PDF с отключенным скачиванием скрываем панель инструментов
	if (isPdf.value && downloadDisabled.value) {
		const suffix = 'toolbar=0&navpanes=0&scrollbar=0'
		return `${url}${url.includes('#') ? '&' : '#'}${suffix}`
	}
	
	return url
})

const downloadName = computed(() => {
	if (title.value) return title.value
	const url = fileUrl.value
	if (!url) return ''
	return url.split('/').pop() || ''
})

const isLocalFile = computed(() => {
	const url = fileUrl.value
	return url.includes('localhost') || url.includes('127.0.0.1')
})

const clampImageZoom = value =>
	Math.min(IMAGE_ZOOM_MAX, Math.max(IMAGE_ZOOM_MIN, value))

const changeImageZoom = delta => {
	imageZoom.value = clampImageZoom(imageZoom.value + delta)
}

const resetImageZoom = () => {
	imageZoom.value = 100
}

const isProtectedPreview = computed(() =>
	downloadDisabled.value && (isImage.value || isPdf.value)
)

const preventProtectedAction = event => {
	if (!isProtectedPreview.value) return
	event.preventDefault()
	event.stopPropagation()
}

const handleProtectedKeydown = event => {
	if (!isProtectedPreview.value) return

	const key = String(event.key || '').toLowerCase()
	const ctrlOrMeta = event.ctrlKey || event.metaKey
	const blockedShortcuts = ['c', 's', 'p', 'u']
	const isBlockedShortcut = ctrlOrMeta && blockedShortcuts.includes(key)
	const isPrintScreen = event.key === 'PrintScreen'
	if (!isBlockedShortcut && !isPrintScreen) return

	event.preventDefault()
	event.stopPropagation()

	if (isPrintScreen && navigator?.clipboard?.writeText) {
		navigator.clipboard.writeText('').catch(() => {})
	}
}

watch(fileUrl, () => {
	resetImageZoom()
})

onMounted(() => {
	window.addEventListener('keydown', handleProtectedKeydown, true)
})

onBeforeUnmount(() => {
	window.removeEventListener('keydown', handleProtectedKeydown, true)
})

const goBack = () => {
	// 1) Реальный "назад" по истории (как в браузере)
	const historyState = router?.options?.history?.state
	if (historyState?.back || window.history.length > 1) {
		router.back()
		return
	}

	// 2) Если историю использовать нельзя (например, открыли страницу напрямую), пробуем referrer из query
	const referrer = route.query.referrer
	if (referrer) {
		router.push(referrer)
		return
	}

	// 3) Фолбэк: отправляем на дашборд по роли
	const { useAppStore } = require('@/stores/useAppStore')
	const appStore = useAppStore()
	const user = appStore.user?.value || appStore.user
	const roles = user?.roles || []

	if (roles.includes('teacher')) router.push('/teacher/dashboard')
	else if (roles.includes('admin')) router.push('/admin/dashboard')
	else router.push('/student-dashboard')
}
</script>

<style scoped>
.document-viewer {
	min-height: 100vh;
	background: linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%);
	display: flex;
	flex-direction: column;
	color: #1f2937;
}

/* Компенсация zoom для разных разрешений */
@media (max-width: 1366px) {
	.document-viewer {
		/* zoom: 0.711, компенсация: 1 / 0.711 = 1.407 */
		min-height: calc(100vh * 1.407);
	}
}

@media (min-width: 1367px) and (max-width: 1600px) {
	.document-viewer {
		/* zoom: 0.85, компенсация: 1 / 0.85 = 1.176 */
		min-height: calc(100vh * 1.176);
	}
}

@media (min-width: 1601px) and (max-width: 1800px) {
	.document-viewer {
		/* zoom: 0.9, компенсация: 1 / 0.9 = 1.111 */
		min-height: calc(100vh * 1.111);
	}
}

.document-viewer__header {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 16px 24px;
	background: #ffffff;
	border-bottom: 1px solid #e2e8f0;
	box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
}

.document-viewer__back {
	border: 1px solid #cbd5f5;
	background: #e8f1ff;
	color: #1d4ed8;
	padding: 8px 14px;
	border-radius: 10px;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.2s ease, color 0.2s ease;
}

.document-viewer__back:hover {
	background: #dbeafe;
	color: #1e40af;
}

.document-viewer__title {
	flex: 1;
	font-size: 18px;
	font-weight: 600;
	color: #0f172a;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.document-viewer__restriction {
	background: #e0f2fe;
	color: #0369a1;
	border-radius: 999px;
	padding: 6px 12px;
	font-size: 12px;
	font-weight: 600;
	white-space: nowrap;
}

.document-viewer__actions {
	display: flex;
	gap: 10px;
	flex-wrap: wrap;
	justify-content: flex-end;
}

.document-viewer__action {
	border: 1px solid #cbd5e1;
	background: #ffffff;
	color: #334155;
	padding: 8px 14px;
	border-radius: 10px;
	font-size: 14px;
	font-weight: 600;
	text-decoration: none;
	transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.document-viewer__action:hover {
	transform: translateY(-1px);
	box-shadow: 0 6px 14px rgba(30, 64, 175, 0.12);
}

.document-viewer__action--primary {
	background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%);
	color: #ffffff;
	border: none;
}

.document-viewer__body {
	flex: 1;
	padding: 20px 24px 28px;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.document-viewer__frame-wrapper {
	position: relative;
	flex: 1;
	width: 100%;
	min-height: 60vh;
	display: flex;
}

.document-viewer__frame {
	width: 100%;
	flex: 1;
	min-height: 60vh;
	border: none;
	border-radius: 16px;
	background: #ffffff;
	box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.document-viewer__frame-shield {
	position: absolute;
	top: 8px;
	right: 8px;
	width: 140px;
	height: 44px;
	background: transparent;
	pointer-events: auto;
	z-index: 2;
}

.document-viewer__image-viewer {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.document-viewer__zoom-controls {
	display: flex;
	align-items: center;
	gap: 8px;
}

.document-viewer__zoom-btn,
.document-viewer__zoom-reset {
	border: 1px solid #cbd5e1;
	background: #ffffff;
	color: #0f172a;
	border-radius: 8px;
	height: 30px;
	padding: 0 10px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
}

.document-viewer__zoom-btn:disabled,
.document-viewer__zoom-reset:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

.document-viewer__zoom-label {
	min-width: 52px;
	text-align: center;
	font-size: 13px;
	font-weight: 700;
	color: #0f172a;
}

.document-viewer__image-stage {
	position: relative;
	flex: 1;
	min-height: 60vh;
	padding: 20px;
	border-radius: 16px;
	background: #ffffff;
	box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
	overflow: auto;
	display: flex;
	align-items: flex-start;
	justify-content: center;
}

.document-viewer__image-stage--protected {
	user-select: none;
	-webkit-user-select: none;
}

.document-viewer__image {
	max-width: 100%;
	height: auto;
	object-fit: contain;
	transform-origin: top center;
}

.document-viewer__protected-watermark {
	position: absolute;
	inset: 0;
	pointer-events: none;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: clamp(20px, 3vw, 42px);
	font-weight: 700;
	letter-spacing: 0.15em;
	text-transform: uppercase;
	color: rgba(15, 23, 42, 0.08);
}

@supports not (zoom: 1) {
	.document-viewer__image {
		transform: scale(var(--image-zoom));
		width: calc(100% / var(--image-zoom));
	}
}

.document-viewer__empty {
	background: #ffffff;
	padding: 24px;
	border-radius: 16px;
	border: 1px dashed #cbd5e1;
	color: #64748b;
	text-align: center;
}

.document-viewer__hint {
	font-size: 14px;
	color: #64748b;
}

.document-viewer__office-wrapper {
	flex: 1;
	width: 100%;
	min-height: 60vh;
	display: flex;
	flex-direction: column;
}

.document-viewer__local-notice {
	background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
	border: 2px solid #fb923c;
	border-radius: 16px;
	padding: 32px;
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	box-shadow: 0 10px 24px rgba(251, 146, 60, 0.15);
}

.document-viewer__notice-title {
	font-size: 18px;
	font-weight: 600;
	color: #ea580c;
	margin: 0;
}

.document-viewer__notice-text {
	font-size: 14px;
	color: #9a3412;
	margin: 0;
	max-width: 500px;
	line-height: 1.6;
}

.document-viewer__download-btn {
	background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
	color: #ffffff;
	padding: 12px 24px;
	border-radius: 10px;
	font-size: 14px;
	font-weight: 600;
	text-decoration: none;
	transition: transform 0.2s ease, box-shadow 0.2s ease;
	display: inline-block;
	margin-top: 8px;
}

.document-viewer__download-btn:hover {
	transform: translateY(-2px);
	box-shadow: 0 8px 20px rgba(249, 115, 22, 0.3);
}

@media (max-width: 768px) {
	.document-viewer__header {
		flex-direction: column;
		align-items: flex-start;
	}

	.document-viewer__actions {
		width: 100%;
		justify-content: flex-start;
	}

	.document-viewer__body {
		padding: 16px;
	}

	.document-viewer__zoom-controls {
		flex-wrap: wrap;
	}

	.document-viewer__image-stage {
		min-height: 48vh;
		padding: 12px;
	}
}
</style>
